import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PackageSearch, Repeat, MapPinned, LogOut, Pause, Play, Trash2, Plus, MapPin, FileDown, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";
import AddressForm from "@/components/AddressForm";

const STATUS_LABEL = {
  placed: "Placed", confirmed: "Confirmed", packed: "Packed",
  shipped: "Shipped", out_for_delivery: "Out for delivery",
  delivered: "Delivered", cancelled: "Cancelled",
};

export default function Account() {
  const nav = useNavigate();
  const { user, loading, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [addrs, setAddrs] = useState([]);
  const [addingAddr, setAddingAddr] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/subscriptions").then((r) => setSubs(r.data));
    reloadAddrs();
  }, [user, loading, nav]);

  const reloadAddrs = () => api.get("/addresses").then((r) => setAddrs(r.data));

  const toggleSub = async (s) => {
    await api.patch(`/subscriptions/${s.id}`, { active: !s.active });
    setSubs((prev) => prev.map((p) => p.id === s.id ? { ...p, active: !p.active } : p));
    toast.success(s.active ? "Subscription paused" : "Subscription resumed");
  };

  const delAddress = async (id) => {
    await api.delete(`/addresses/${id}`);
    setAddrs((prev) => prev.filter((a) => a.id !== id));
    toast.success("Address removed");
  };

  if (loading || !user) return <div className="max-w-[1400px] mx-auto py-32 text-center text-ink-500">Loading…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
      <div className="flex justify-between items-end mb-10">
        <div>
          <div className="eyebrow">Account</div>
          <h1 className="mt-3 font-display text-5xl text-ink-900">Hello, {user.name || user.email.split("@")[0]}.</h1>
          <div className="mt-2 text-sm text-ink-500">{user.email}</div>
        </div>
        <button onClick={async () => { await logout(); nav("/"); }} className="btn-gold-outline px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2" data-testid="logout-btn">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="bg-transparent border-b hairline rounded-none w-full justify-start h-auto p-0 gap-8">
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-ink-900 data-[state=active]:bg-transparent px-0 pb-3 text-sm">
            <PackageSearch size={14} className="mr-2" /> Orders
          </TabsTrigger>
          <TabsTrigger value="subs" data-testid="tab-subs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-ink-900 data-[state=active]:bg-transparent px-0 pb-3 text-sm">
            <Repeat size={14} className="mr-2" /> Subscriptions
          </TabsTrigger>
          <TabsTrigger value="addr" data-testid="tab-addr" className="rounded-none border-b-2 border-transparent data-[state=active]:border-ink-900 data-[state=active]:bg-transparent px-0 pb-3 text-sm">
            <MapPinned size={14} className="mr-2" /> Addresses
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <div className="text-xs text-ink-500">Recent orders — {orders.length}</div>
            <Link to="/account/orders" className="link-underline text-xs uppercase tracking-widest inline-flex items-center gap-1" data-testid="view-all-orders">
              View all with filters <ArrowRight size={12} />
            </Link>
          </div>
          {orders.length === 0 && <div className="text-ink-500 text-sm">No orders yet.</div>}
          <div className="space-y-4">
            {orders.slice(0, 5).map((o) => (
              <div key={o.id} className="border hairline p-6 bg-cream-50" data-testid={`order-${o.order_number}`}>
                <div className="flex justify-between flex-wrap gap-4">
                  <div>
                    <div className="eyebrow">Order</div>
                    <div className="mt-1 font-mono text-sm tracking-wider">#{o.order_number}</div>
                    <div className="mt-1 text-xs text-ink-500">{new Date(o.created_at).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="eyebrow">Status</div>
                    <div className="mt-1 text-sm">{STATUS_LABEL[o.status] || o.status}</div>
                    {o.eta && <div className="text-xs text-ink-500 mt-1">ETA {o.eta}</div>}
                  </div>
                  <div>
                    <div className="eyebrow">Total</div>
                    <div className="mt-1 font-display text-lg">₹{o.total.toFixed(0)}</div>
                  </div>
                  <div className="flex items-end gap-3">
                    <Link to={`/track?n=${o.order_number}`} className="link-underline text-sm" data-testid={`account-track-${o.order_number}`}>Track →</Link>
                    <a
                      href={`${API}/orders/${encodeURIComponent(o.order_number)}/invoice.pdf`}
                      target="_blank" rel="noopener noreferrer"
                      className="link-underline text-sm inline-flex items-center gap-1"
                      data-testid={`account-invoice-${o.order_number}`}
                    >
                      <FileDown size={12} /> Invoice
                    </a>
                  </div>
                </div>
                <div className="mt-4 flex gap-3 overflow-auto">
                  {o.items.map((it, i) => (
                    <img key={i} src={it.image} alt="" className="w-14 h-16 object-cover bg-cream-200" title={`${it.name} × ${it.quantity}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="subs" className="mt-8">
          {subs.length === 0 && <div className="text-ink-500 text-sm">No active subscriptions.</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {subs.map((s) => (
              <div key={s.id} className="border hairline p-6 bg-cream-50" data-testid={`sub-${s.id}`}>
                <div className="flex justify-between">
                  <div>
                    <div className="eyebrow">Cadence</div>
                    <div className="mt-1 font-display text-lg">{s.cadence}</div>
                    <div className="text-xs text-ink-500 mt-1">Next: {new Date(s.next_order_at).toLocaleDateString()}</div>
                  </div>
                  <button onClick={() => toggleSub(s)} className={"px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-2 " + (s.active ? "btn-gold-outline" : "btn-primary-ink")}>
                    {s.active ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="addr" className="mt-8">
          <div className="flex justify-end mb-4">
            {!addingAddr && (
              <button
                onClick={() => setAddingAddr(true)}
                className="btn-primary-ink px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2"
                data-testid="account-add-address-btn"
              >
                <Plus size={14} /> Add address
              </button>
            )}
          </div>

          {addingAddr && (
            <div className="mb-6">
              <AddressForm
                defaultName={user.name || ""}
                onSaved={async () => { await reloadAddrs(); setAddingAddr(false); }}
                onCancel={() => setAddingAddr(false)}
              />
            </div>
          )}

          {addrs.length === 0 && !addingAddr && (
            <div className="text-ink-500 text-sm border hairline p-8 bg-cream-50 text-center">
              No saved addresses. Add one to speed up future checkouts.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {addrs.map((a) => (
              <div key={a.id} className="border hairline p-6 bg-cream-50" data-testid={`addr-${a.id}`}>
                <div className="flex justify-between gap-3">
                  <div className="min-w-0">
                    <div className="eyebrow">{a.label}{a.is_default ? " · Default" : ""}</div>
                    <div className="mt-1 text-sm font-medium">{a.name}</div>
                    <div className="text-sm text-ink-700">{a.line1}{a.line2 ? ", " + a.line2 : ""}</div>
                    <div className="text-sm text-ink-700">{a.city}, {a.state} {a.pincode}</div>
                    <div className="text-xs text-ink-500 mt-1">{a.phone}</div>
                    {a.latitude && a.longitude && (
                      <a
                        href={`https://www.google.com/maps?q=${a.latitude},${a.longitude}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-[11px] text-gold-500 hover:text-ink-900"
                        data-testid={`addr-map-link-${a.id}`}
                      >
                        <MapPin size={11} /> View on map ↗
                      </a>
                    )}
                  </div>
                  <button onClick={() => delAddress(a.id)} className="text-ink-500 hover:text-ink-900 shrink-0" aria-label="Remove"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
